import { Box, Grid, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from '@mui/material';
import circle from '@/images/zirkel.png';
import station from '@/images/stationentraining.png';
import fitnessfatique from '@/images/fitness-fatique.png';
import superset from '@/images/superset.png';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DefaultContainer } from '@/components/DefaultContainer';
import Link from '@mui/material/Link';

const appName = 'Witness the Fitness';

const techniques = [
  {
    name: '🚆 Station Training',
    description: 'Complete all sets and reps for one exercise at a single station before moving to the next.',
    benefits: 'Focused effort on each muscle group, good for building strength and progressive overload.',
    disadvantages: 'Can be time-consuming, may require long rest periods, less cardiovascular benefit.',
    examples: '3 sets of bench press before moving to squats, then lat pulldowns.',
  },
  {
    name: '🔄 Circuit Training',
    description: 'Perform a series of exercises in sequence with minimal rest, often targeting different muscle groups.',
    benefits: 'Efficient for time, improves muscular endurance and cardiovascular fitness.',
    disadvantages: 'Less focus on maximal strength, can be fatiguing, not ideal for heavy lifting.',
    examples: 'Push-ups → Squats → Jumping jacks → Plank, repeat for 3 rounds.',
  },
  {
    name: '⚡ Superset Training',
    description: 'Pair two exercises performed back-to-back with little or no rest, often for opposing or related muscle groups.',
    benefits: 'Saves time, increases workout intensity, enhances muscle pump.',
    disadvantages: 'Challenging for beginners, recovery time reduced, performance may drop on second exercise.',
    examples: 'Bicep curls + Tricep dips, Bench press + Bent-over rows.',
  },
];

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const UserGuide = () => {
  const { pageIndex } = useParams();

  const [value, setValue] = useState(pageIndex ? Number(pageIndex) : 0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  return (
    <DefaultContainer sx={{ minHeight: 1700 }}>
      <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
        <Tab label="How to Train with the App" {...a11yProps(0)} />

        <Tab label="Training 101" {...a11yProps(1)} />
        <Tab label="Videos and Links" {...a11yProps(2)} />
        {/* <Tab label="Item Three" {...a11yProps(2)} /> */}
      </Tabs>
      <CustomTabPanel index={0} value={value}>
        <HowToTrainWithTheAppTab />
      </CustomTabPanel>
      <CustomTabPanel index={1} value={value}>
        <Training101Tab />
      </CustomTabPanel>
      <CustomTabPanel index={2} value={value}>
        <Grid size={12}>
          <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
            Best Song Ever
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/NDWgtB_MD24?si=XpkYuXOFlK5_0025"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ></iframe>
          </Box>
          <Typography variant="body1" sx={{ textAlign: 'center' }} gutterBottom>
            The Best Song from the World's Best Musician: Witness the Fitness Song by Roots Manuva
          </Typography>
          <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
            Web UI - How To: Creating Sets, Creating Exercises and Importing Exercises
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/nYMf8V0y51A?si=690cqfDVBRgVkXZN"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </Box>
          <Typography variant="body1" sx={{ textAlign: 'center' }} gutterBottom>
            Video on youtube that explains how to use the web ui to create sets how to create exercises and how to use the exercise import function and how copying exercises
            between sets basically works.
          </Typography>
          <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
            Fitness related Links
          </Typography>
          <Stack sx={{ alignItems: 'center' }}>
            <Link href="https://muscleandstrengthpyramids.com/">The Muscle and strength Pyramid Books</Link>
            <Link href="https://www.strongerbyscience.com/">Stronger by Science</Link>
            <Link href="https://www.innerbody.com/image/musfov.html">A 3D Zoomable Muscle Map of the Body.</Link>
          </Stack>
        </Grid>
      </CustomTabPanel>
    </DefaultContainer>
  );
};

const Training101Tab = () => {
  return (
    <>
      <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
        Training 101 🚆 🚉 🚆
      </Typography>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ my: 4 }}>
          In this quick tutorial I'll give you an overview of common training styles, their advantages and disadvantages and show you how to train them in "{appName}". The most
          commonly known training styles in sport science are <em>Station Training</em>, <em>Circuit Training</em> and <em>Superset Training</em>.
        </Typography>
      </Box>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Training Techniques
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Technique</strong>
              </TableCell>
              <TableCell>
                <strong>Description</strong>
              </TableCell>
              <TableCell>
                <strong>Benefits</strong>
              </TableCell>
              <TableCell>
                <strong>Disadvantages</strong>
              </TableCell>
              <TableCell>
                <strong>Examples</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {techniques.map((technique, index) => (
              <TableRow key={index}>
                <TableCell>{technique.name}</TableCell>
                <TableCell>{technique.description}</TableCell>
                <TableCell>{technique.benefits}</TableCell>
                <TableCell>{technique.disadvantages}</TableCell>
                <TableCell>{technique.examples}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h5" sx={{ my: 4 }}>
        Breathing for strength training
      </Typography>
      <Box sx={{ textAlign: 'justify' }}>
        <Typography sx={{ my: 4 }}>
          Proper breathing is essential for safe and effective strength training. A useful guideline is to inhale during the lowering or resetting phase of a movement and exhale
          during the phase where you're producing force to move the weight. Even though both parts of a lift can feel challenging, the “force-producing phase” is the moment you’re
          actively driving or pulling the weight in the intended direction (e.g., standing up from a squat, pressing a bar away from your chest, or pulling yourself upward).
          Matching your breath to these phases helps stabilize your core, maintain form, and reduce unnecessary strain.
        </Typography>
        <Typography sx={{ my: 4 }}>
          Footnote: This pattern isn’t universal. In certain situations—such as heavy squats, deadlifts, or other maximal-effort lifts—lifters often use a brief Valsalva maneuver,
          taking a deep breath and holding it to create strong intra-abdominal pressure before exhaling near the top. This is an advanced technique and should be used with proper
          guidance.
        </Typography>
      </Box>
      <BreathingTable />
      <TrainingModelsMUI />
      <Box>
        <Typography variant="h4" gutterBottom>
          Special Training Styles
        </Typography>
        Special training styles are designed to target specific performance qualities within the neuromuscular system. Maximal strength training focuses on lifting heavy loads for
        low repetitions to increase absolute force production. Plyometric training uses explosive movements to enhance the stretch–shortening cycle and improve power and reactive
        ability. Strength endurance training involves moderate loads and higher repetitions to improve a muscle’s ability to sustain force over time. Together, these methods
        develop force, speed, and fatigue resistance for well-rounded athletic performance.
        <Typography variant="h5" sx={{ my: 4 }}>
          Maximum strength training
        </Typography>
        To train maximum strength (often called maximal strength or heavy resistance training), the goal is to increase the highest force the neuromuscular system can produce. This
        is achieved through compound lifts such as squats, deadlifts, presses, and pulls performed with heavy loads—generally 85–100% of one-repetition maximum (1RM)—for low
        repetitions (1–5 reps per set) and multiple sets (3–6 sets). Rest periods should be long (2–5 minutes) to allow near-complete recovery and maintain high force output.
        Progressive overload is essential: gradually increasing load, improving technique, or increasing total volume over time. Power-oriented maximal training may also
        incorporate heavy lifts performed with maximal concentric intent, as well as contrast or complex training (pairing heavy lifts with plyometric movements) to enhance force
        expression. Together, structured plyometric training and well-planned maximal strength training create a comprehensive program that develops both force capacity and the
        ability to express it rapidly.
        <Typography variant="h5" sx={{ my: 4 }}>
          Hypertrophy training training
        </Typography>
        Hypertrophy training (muscle growth training) focuses on increasing the size of muscle fibers by creating mechanical tension, metabolic stress, and controlled muscle
        damage. It typically uses moderate loads (about 60–80% of one-repetition maximum) for 6–12 repetitions per set, with 3–6 sets per exercise and rest periods of 60–120
        seconds. This repetition range allows for sufficient time under tension to stimulate muscle growth while maintaining proper technique. The main benefits of hypertrophy
        training include increased muscle mass, improved muscular strength, enhanced joint stability, and a higher resting metabolic rate due to greater lean body mass. It also
        provides a strong structural foundation that can support both maximal strength and power development.
        <Typography variant="h5" sx={{ my: 4 }}>
          Strength endurance training
        </Typography>
        Strength endurance training focuses on improving a muscle’s ability to produce force repeatedly over an extended period. It typically involves moderate to light loads
        (about 40–70% of one-repetition maximum) performed for higher repetitions, usually 12–20+ reps per set, with shorter rest intervals (30–90 seconds). This style of training
        enhances muscular stamina, improves fatigue resistance, and supports better performance in prolonged activities. In addition to muscular benefits, strength endurance
        training positively impacts the cardiovascular system by increasing heart rate and circulation during sustained efforts, thereby improving cardiovascular efficiency and
        overall aerobic capacity.
        <Typography variant="h5" sx={{ my: 4 }}>
          Why should you perform plyometric training? - The stretch–shortening cycle (SSC)
        </Typography>
        To train plyometrics effectively, the focus should be on maximizing the efficiency of the stretch–shortening cycle (SSC) through explosive, high-quality movements. Sessions
        should emphasize low to moderate volume, full recovery between sets, and maximal intent on every repetition. Beginners should start with low-intensity drills such as
        skipping, pogo jumps, and low box jumps to develop landing mechanics and tendon stiffness. As competency improves, training can progress to higher-intensity exercises such
        as depth jumps, bounding, and single-leg hops. Ground contact time should be short, posture controlled, and landings quiet and stable. Plyometrics are best performed when
        fresh—early in a session—and typically 1–3 times per week depending on intensity and overall workload.
      </Box>
    </>
  );
};

const HowToTrainWithTheAppTab = () => {
  return (
    <>
      <Grid sx={{ justifyContent: 'center' }} gap={10} container>
        <Grid size={12}>
          <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
            How to train 🚆 with the App
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <iframe
              width="728"
              height="409.5"
              src="https://www.youtube.com/embed/ZS1OUljVnI0?si=x3-w5WytVn7BLHK3"
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            ></iframe>
          </Box>
          <Typography variant="body1" sx={{ textAlign: 'center' }} gutterBottom>
            Video on youtube that explains the basic features and how to use the app.
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant="h5" sx={{ textAlign: 'center', mt: 3 }} gutterBottom>
            Examples for using training techniques (see training 101 tab)
          </Typography>
        </Grid>
        <Grid container sx={{ alignItems: 'flex-start', justifyContent: 'center', alignContent: 'flex-start' }} size={3}>
          <img style={{ width: 200 }} src={circle}></img>
          <Typography sx={{ mt: 3 }}>
            <b>Circuit Training Example</b> - Design a workout with three rows and include as many body zones as you’d like to target. For example, you could set up six leg
            exercises (two per row across three rows), six arm exercises, six core exercises, and six back exercises.
          </Typography>
          <Typography sx={{ mt: 3 }}>
            Perform the exercises in a vertical, top-to-bottom order. Circuit training usually emphasizes lower intensity per exercise with short rest periods between them. Use the
            “Last Exercise” timer to monitor your breaks and keep the workout efficient and consistent.
          </Typography>
        </Grid>
        <Grid container sx={{ alignItems: 'flex-start', justifyContent: 'center', alignContent: 'flex-start' }} size={3}>
          <img style={{ width: 200 }} src={station}></img>
          <Typography sx={{ mt: 3 }}>
            <b>Station Training Example</b> – Create a workout with 5 rows, focusing on a full-body approach. A simple setup could include 5 squats, 5 pull-ups, 5 bicep curls, and
            5 face pulls. You can mix and match exercises to target different muscle groups, ensuring each station challenges a specific area.
          </Typography>
          <Typography sx={{ mt: 3 }}>
            Complete the exercises in horizontal order, moving left to right across each row. Rest briefly between stations if needed, and use the “Last Exercise” timer to track
            breaks and keep the workout consistent and efficient.
          </Typography>
        </Grid>
        <Grid container sx={{ alignItems: 'flex-start', justifyContent: 'center', alignContent: 'flex-start' }} size={3}>
          <img style={{ width: 200 }} src={superset}></img>
          <Typography sx={{ mt: 3 }}>
            <b>Superset Training Example</b> – Create a workout with 4 or 5 rows. Structure the setup by pairing muscles with their antagonists (e.g., biceps vs. triceps) or by
            choosing different exercises for the same muscle group. A simple starting point could be 2 arm exercises like bicep curls and face pulls, and 2 leg exercises like
            squats and deadlifts.
          </Typography>
          <Typography sx={{ mt: 3 }}>
            Perform the exercises in an alternating row pattern: row 1, row 2, row 1, row 2, and so on, continuing with row 3, row 4, row 3, row 4.
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

const BreathingTable = () => {
  const rows = [
    { exercise: 'Push-up', phase: 'Lowering (down)', breath: 'Inhale' },
    { exercise: 'Push-up', phase: 'Pushing up', breath: 'Exhale' },

    { exercise: 'Bench Press', phase: 'Lowering bar', breath: 'Inhale' },
    { exercise: 'Bench Press', phase: 'Pressing bar up', breath: 'Exhale' },

    { exercise: 'Shoulder Press', phase: 'Lowering dumbbells/bar', breath: 'Inhale' },
    { exercise: 'Shoulder Press', phase: 'Pressing upward', breath: 'Exhale' },

    { exercise: 'Pull-up', phase: 'Lowering down', breath: 'Inhale' },
    { exercise: 'Pull-up', phase: 'Pulling up', breath: 'Exhale' },

    { exercise: 'Row (seated/DB/BB)', phase: 'Returning weight forward', breath: 'Inhale' },
    { exercise: 'Row (seated/DB/BB)', phase: 'Pulling toward you', breath: 'Exhale' },

    { exercise: 'Squat', phase: 'Going down', breath: 'Inhale' },
    { exercise: 'Squat', phase: 'Standing up', breath: 'Exhale' },

    { exercise: 'Deadlift', phase: 'Lowering weight', breath: 'Inhale' },
    { exercise: 'Deadlift', phase: 'Lifting weight', breath: 'Exhale' },

    { exercise: 'Lunge', phase: 'Stepping down', breath: 'Inhale' },
    { exercise: 'Lunge', phase: 'Pushing back up', breath: 'Exhale' },

    { exercise: 'Crunch / Sit-up', phase: 'Lowering back', breath: 'Inhale' },
    { exercise: 'Crunch / Sit-up', phase: 'Curling up', breath: 'Exhale' },

    { exercise: 'Plank', phase: 'Static hold', breath: 'Breathe normally' },

    { exercise: 'Running', phase: 'Rhythmic steps', breath: 'Inhale for 2–3 steps / Exhale for 2–3 steps' },
  ];
  return (
    <TableContainer component={Paper}>
      <Typography variant="h6" sx={{ p: 2 }}>
        Breathing Guide for Exercises
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Exercise</strong>
            </TableCell>
            <TableCell>
              <strong>Movement Phase</strong>
            </TableCell>
            <TableCell>
              <strong>Breathing Instruction</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              <TableCell>{row.exercise}</TableCell>
              <TableCell>{row.phase}</TableCell>
              <TableCell>{row.breath}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const referenceRows = [
  {
    model: 'Supercompensation',
    description:
      'Conceptual model describing the performance drop after training, recovery back to baseline, and a temporary overshoot (supercompensation) if recovery is sufficient.',
    mainRef: 'General overview',
    links: [
      {
        label: 'Supercompensation (Wikipedia)',
        url: 'https://en.wikipedia.org/wiki/Supercompensation',
      },
    ],
  },
  {
    model: 'Fitness–Fatigue',
    description:
      'The FFM is based on the “General Adaption Syndrome (GAS) Principle” which is also known as the Single Factor Model because it only looks at recovery and doesn’t take fatigue into consideration. What this means is that if we were to look at our progress in the gym it would only be dictated by our recovery, so basically, if we simply were to relax a little bit more, we would see our progress to rise again. However, this isn’t how people work and training isn’t the only stressor that causes fatigue.',
    mainRef: 'Banister impulse–response model',
    links: [
      {
        // closest Wikipedia-style entry describing the model
        label: 'Fitness–fatigue model ',
        url: 'https://revivestronger.com/training-volume/',
      },
    ],
  },
];

export default function TrainingModelsMUI() {
  return (
    <Box sx={{ pt: 2, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Supercompensation & Fitness–Fatigue Models
      </Typography>

      {/* Supercompensation text */}
      <Typography variant="h6" gutterBottom>
        Supercompensation Model
      </Typography>

      <Typography variant="body1" paragraph>
        The <strong>supercompensation model</strong> describes how performance changes after a single training bout. First, a training load causes a temporary drop in performance
        due to fatigue. During recovery, performance returns to its original baseline and, for a limited time, rebounds above baseline – the{' '}
        <strong>supercompensation phase</strong>. If the next training stimulus is timed to coincide with this peak, long-term improvements are optimized; if it comes too early,
        fatigue accumulates, and if it comes too late, the supercompensation effect is lost.
      </Typography>

      {/* Fitness–Fatigue text with Wikipedia-style summary */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Fitness–Fatigue (Banister) Model
      </Typography>
      <Typography variant="body1" paragraph>
        The <strong>fitness–fatigue model</strong>, also known as the <strong>Banister impulse–response model</strong>, treats each training session as an input that simultaneously
        produces two opposing after-effects:
      </Typography>
      <Typography variant="body1" component="ul">
        <li>
          a <strong>fitness effect</strong> (positive) that builds and decays relatively slowly
        </li>
        <li>
          a <strong>fatigue effect</strong> (negative) that appears quickly and dissipates more rapidly
        </li>
      </Typography>

      <Typography variant="body1" paragraph sx={{ mt: 1 }}>
        At any given time, <strong>performance is modeled as Fitness − Fatigue</strong>. Because fatigue decays faster than fitness, performance can rise (for example, during a
        taper) even while the overall training load is reduced. This framework is widely used to analyze training load, estimate readiness, and plan peaking strategies.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 5 }}>
        <img style={{ width: 384, height: 256 }} src={fitnessfatique} />
        <TableContainer component={Paper} sx={{ mt: 0 }}>
          <Table size="small" aria-label="training model references">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Model</strong>
                </TableCell>
                <TableCell>
                  <strong>Short Description</strong>
                </TableCell>
                <TableCell>
                  <strong>Main Reference</strong>
                </TableCell>
                <TableCell>
                  <strong>Links</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referenceRows.map((row) => (
                <TableRow key={row.model}>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{row.mainRef}</TableCell>
                  <TableCell>
                    {row.links.map((link) => (
                      <Box key={link.url}>
                        <Link href={link.url} target="_blank" rel="noopener noreferrer">
                          {link.label}
                        </Link>
                      </Box>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* References table */}
    </Box>
  );
}
